'use client';

import {useEffect} from 'react';
import {usePathname} from 'next/navigation';
import {GoogleAnalytics} from '@next/third-parties/google';
import {useReportWebVitals} from 'next/web-vitals';
import type {NextWebVitalsMetric} from 'next/app';
import {useConsent} from '@/lib/useConsent';
import {track, trackClick} from '@/lib/analytics';
import {GA_MEASUREMENT_ID} from '@/config';

const SCROLL_MILESTONES = [25, 50, 75, 90, 100];

// Module-level (stable) callback: an inline function would re-register on every render
// and report each metric multiple times.
const reportWebVitals = (metric: NextWebVitalsMetric) => {
  track('web_vitals', {
    metric_name: metric.name,
    metric_id: metric.id,
    value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
  });
};

/**
 * GA4 (only when enabled for this environment and after cookie consent) plus page-wide tracking:
 * click handling (data-track attributes, tel:/mailto:, link areas), scroll depth, copy, Web Vitals.
 * Events are dropped by `track()` unless analytics is enabled and consent was given.
 */
const Analytics = () => {
  const consent = useConsent();
  const pathname = usePathname();

  useReportWebVitals(reportWebVitals);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.target instanceof HTMLElement) {
        trackClick(event.target);
      }
    };
    const onCopy = () => {
      const length = window.getSelection()?.toString().length ?? 0;
      if (length > 0) {
        track('copy_text', {text_length: length});
      }
    };
    document.addEventListener('click', onClick, {capture: true});
    document.addEventListener('copy', onCopy);
    return () => {
      document.removeEventListener('click', onClick, {capture: true});
      document.removeEventListener('copy', onCopy);
    };
  }, []);

  // Scroll depth milestones, reset on every page change.
  useEffect(() => {
    const reached = new Set<number>();
    let frame = 0;
    const measure = () => {
      frame = 0;
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const percent = scrollable <= 0 ? 100 : (window.scrollY / scrollable) * 100;
      for (const milestone of SCROLL_MILESTONES) {
        if (percent >= milestone - 0.5 && !reached.has(milestone)) {
          reached.add(milestone);
          track('scroll_depth', {percent: milestone});
        }
      }
    };
    const onScroll = () => {
      frame ||= window.requestAnimationFrame(measure);
    };
    window.addEventListener('scroll', onScroll, {passive: true});
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.cancelAnimationFrame(frame);
    };
  }, [pathname]);

  if (!GA_MEASUREMENT_ID || consent !== 'accepted') {
    return null;
  }
  return <GoogleAnalytics gaId={GA_MEASUREMENT_ID} />;
};

export default Analytics;
